import { Component, DestroyRef, effect, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { sharedImports } from '../../sharedImports';
import { provideTranslocoScope } from '@jsverse/transloco';
import { ReactiveFormsModule } from '@angular/forms';
import { ImageService } from '@api';
import { ArtIconComponent } from '../art-icon/art-icon.component';
import { map } from 'rxjs';
import { ImageUrlPipe } from '@pipes/image-url.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ImageUploadResult } from '@utils/interfaces';

@Component({
  selector: 'art-drag-and-drop',
  imports: [sharedImports, ReactiveFormsModule, ArtIconComponent, ImageUrlPipe],
  providers: [provideTranslocoScope('dropzone')],
  templateUrl: './drag-and-drop.component.html',
  styleUrl: './drag-and-drop.component.scss',
  standalone: true,
})
export class DragAndDropComponent {
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  uploadedImage = output<ImageUploadResult>();
  showError = input(false);
  errorMessage = input('');
  fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  imageService = inject(ImageService);
  uploadedImageUrl = signal<string | null>(null);
  isUploading = signal(false);
  showInvalidTooltip = signal(false);

  constructor() {
    effect(() => {
      if (this.showError()) {
        this.scrollIntoView();
      }
    });
  }

  dragoverHandler(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const items = Array.from(event.dataTransfer?.items ?? []);
    const isSingleImage = items.length === 1 && this.isImageItem(items[0]);

    if (this.uploadedImageUrl() || !isSingleImage) {
      event.dataTransfer!.dropEffect = 'none';
      this.showInvalidTooltip.set(true);
    } else {
      event.dataTransfer!.dropEffect = 'copy';
      this.showInvalidTooltip.set(false);
    }
  }

  dragleaveHandler(): void {
    this.showInvalidTooltip.set(false);
  }

  isImageItem(item: DataTransferItem): boolean {
    return item.kind === 'file' && item.type.startsWith('image/');
  }

  dropHandler(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isUploading() || this.uploadedImageUrl()) return;

    const files = event.dataTransfer?.files;
    if (!files || files.length !== 1) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) return;

    this.uploadFile(file);
  }

  prepareSelectedFileForUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file && this.uploadedImageUrl() === null && !this.isUploading()) {
      this.uploadFile(file);
    }
  }

  uploadFile(file: File) {
    this.isUploading.set(true);
    const newFile = new File([file], file.name, { type: file.type });
    this.imageService
      .createImage(newFile, 'response')
      .pipe(
        map((response) => ({
          location: response.headers.get('Location'),
          imageId: response.body?.imageId,
        })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ location, imageId }) => {
          this.isUploading.set(false);
          if (location && imageId) {
            this.uploadedImageUrl.set(location);
            this.uploadedImage.emit({ imageId: imageId, imageUrl: location });
          }
        },
        error: () => this.isUploading.set(false),
      });
  }

  deleteImage() {
    this.isUploading.set(true);
    this.uploadedImageUrl.set(null);
    this.uploadedImage.emit({ imageId: null, imageUrl: null });
    this.isUploading.set(false);
  }

  reset() {
    this.deleteImage();
  }

  scrollIntoView() {
    this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.fileInputRef()?.nativeElement.focus({ preventScroll: true });
  }
}
